import { useQuery } from '@tanstack/react-query';
import { ApiProgramEvent, AppProgramEvent } from '@/entities/program';
import { axiosAPI } from './axios-instance';
import { ApiResponse } from './lib/types';
import { transformConstructorFromApi } from './lib/utils/transform-constructor';

export type ApiProgramRequest = Partial<{
    directions: number[];
    day: number;
    days: number[];
    q: string;
    is_free: number;
    for_visitors: 1 | 0;
    participation_formats: number[];
    tags: number[];
}>;

type Params = {
    params?: ApiProgramRequest;
    signal?: AbortSignal;
};

export type AppProgramResponse = ApiResponse<{
    totalCount: number;
    items: AppProgramEvent[];
}>;

export type ApiProgramResponse = ApiResponse<{
    items: ApiProgramEvent[];
    totalCount: number;
}>;

const transformApiResponse = (response: ApiProgramResponse): AppProgramResponse => {
    return {
        ...response,
        data: {
            totalCount: response.data?.totalCount || (response.data?.items ?? []).length,
            items: (response.data?.items ?? []).map((item) => ({
                id: item.id,
                slug: item.slug || '',
                name: item.name || '',
                content: transformConstructorFromApi(item.content || []),
                description: item.description || '',
                prefix: item.prefix || '',
                time: item.time,
                image: item.image || {},
                place: item.place,
                directions: item.directions,
                participationFormats: item.participation_formats || [],
                participationDescription: item.participation_description || '',
                day: item.day,
                companies: item.companies || [],
                gallery: item.gallery || [],
                tags: item.tags || [],
            })),
        },
    };
};

export const getProgram = ({ params = {}, signal }: Params = {}): Promise<AppProgramResponse> =>
    axiosAPI.get<ApiProgramResponse>('/v2/programs', { params, signal }).then((res) => transformApiResponse(res.data));

export const useProgramQuery = (data: Params, initialData?: AppProgramResponse) =>
    useQuery({
        queryKey: ['program', data],
        queryFn: () => getProgram(data),
        placeholderData: (previousData) => previousData ?? initialData,
        refetchOnMount: true,
    });
